import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { UserConfigService } from '@core/services';
import { type CrmProvider } from '@core/services';
import { NotificationService } from '@core/services';
import { AmplitudeService } from '@core/services';
import { LicenseService, AccessContext } from '@core/services';
import { SupabaseService } from '@core/services/supabase/supabase.service';
import { MainWrapperComponent } from '@core/component';
import { debounceTime } from 'rxjs/operators';


@Component({
  selector: 'app-options',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MainWrapperComponent],
  templateUrl: './options.component.html',
  styleUrls: ['./options.component.scss']
})
export class OptionsComponent implements OnInit, OnDestroy {
  private readonly CRM_LABELS: Record<CrmProvider, string> = {
    salesforce: 'Salesforce',
    zoho: 'Zoho',
    hubspot: 'HubSpot',
    pipedrive: 'Pipedrive',
  };

  optionsForm!: FormGroup;
  isLoading = false;
  isSaving = false;
  isCrmActionLoading = false;
  crmActionProvider: CrmProvider | null = null;
  crmActionType: 'connect' | 'disconnect' | null = null;
  connectedCrmProvider: CrmProvider | null = null;
  userDefaultDomain: string | null = null;
  newBcDomain: string = '';
  newIgnoredDomain: string = '';
  bcDomainEnabledState: Map<number, boolean> = new Map();

  // License section
  licenseContext: AccessContext | null = null;
  organizationName: string | null = null;
  private _licenseSub?: Subscription;
  private _formSub?: Subscription;
  saveState: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  private saveTimeout: any;

  // List of free email domains that should not have the toggle option
  private readonly FREE_EMAIL_DOMAINS = [
    'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 
    'icloud.com', 'aol.com', 'protonmail.com', 'mail.com',
    'zoho.com', 'yandex.com', 'gmx.com', 'live.com'
  ];

  constructor(
    private fb: FormBuilder,
    private userConfigService: UserConfigService,
    private notificationService: NotificationService,
    private amplitudeService: AmplitudeService,
    private licenseService: LicenseService,
    private supabaseService: SupabaseService,
  ) {}

  get daysUntilExpiry(): number | null {
    return this.licenseService.daysUntilExpiry;
  }

  get isFreemium(): boolean {
    return this.licenseService.isFreemium;
  }

  get planLabel(): string {
    switch (this.licenseContext?.license_type) {
      case 'free_trial': return 'Free Trial';
      case 'freemium': return 'Free';
      case 'companion': return 'Companion';
      case 'pro': return 'Pro';
      case 'enterprise': return 'Enterprise';
      default: return '—';
    }
  }

  get licenseEndFormatted(): string {
    const end = this.licenseContext?.license_end;
    if (!end) return '—';
    return new Date(end).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  get licenseStatusLabel(): string {
    const state = this.licenseContext?.effective_license_state;
    switch (state) {
      case 'active': {
        const d = this.daysUntilExpiry;
        if (d !== null && d <= 3) return `Active · expires ${d === 0 ? 'today' : 'in ' + d + ' days'}`;
        return 'Active';
      }
      case 'in_grace': return 'Grace period';
      case 'expired': return 'Expired';
      case 'suspended': return 'Suspended';
      case 'none': return 'No license';
      default: return '—';
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.loadConfig();
    // Subscribe to license context
    this._licenseSub = this.licenseService.context$.subscribe(ctx => {
      this.licenseContext = ctx;
      this.loadOrganizationName();
    });
    this.licenseContext = this.licenseService.getAccessContext();
    this.loadOrganizationName();

    this._formSub = this.optionsForm.valueChanges
      .pipe(debounceTime(1500))
      .subscribe(() => {
        if (!this.isLoading) {
          this.save();
        }
      });
  }

  async loadOrganizationName(): Promise<void> {
    if (!this.licenseContext?.organization_id || this.organizationName) return;
    try {
      const supabase = await this.supabaseService.getClient();
      const { data } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', this.licenseContext.organization_id)
        .single();
      
      if (data?.name) {
        this.organizationName = data.name;
      }
    } catch (err) {
      console.error('Failed to load org name', err);
    }
  }

  ngOnDestroy(): void {
    this._licenseSub?.unsubscribe();
    this._formSub?.unsubscribe();
    if (this.saveTimeout) clearTimeout(this.saveTimeout);
  }

  private initForm(): void {
    // Get user's default domain first
    this.userDefaultDomain = this.userConfigService.getUserDefaultDomain();
    
    this.optionsForm = this.fb.group({
      ignoredDomains: this.fb.array([]),
      bcIgnoreDefaultDomain: [{ value: this.isFreeEmailDomain, disabled: this.isFreeEmailDomain }],
      bcDomains: this.fb.array([]),
      pushMode: ['notes'],
      autoSync: [false],
      notetakerMode: [false]
    });
  }

  get ignoredDomainsArray(): FormArray {
    return this.optionsForm.get('ignoredDomains') as FormArray;
  }

  get bcDomainsArray(): FormArray {
    return this.optionsForm.get('bcDomains') as FormArray;
  }

  get bcIgnoreDefaultDomain(): FormControl {
    return this.optionsForm.get('bcIgnoreDefaultDomain') as FormControl;
  }

  get isFreeEmailDomain(): boolean {
    if (!this.userDefaultDomain) return false;
    return this.FREE_EMAIL_DOMAINS.includes(this.userDefaultDomain.toLowerCase());
  }

  private async loadConfig(): Promise<void> {
    this.isLoading = true;
    try {
      // Load ignored domains
      const ignoredDomains = await firstValueFrom(this.userConfigService.loadIgnoredDomains());
      this._setDomainControls(this.ignoredDomainsArray, ignoredDomains);

      // Load battle card domains
      const bcConfig = await this.userConfigService.loadBattleCardDomains();
      this.bcIgnoreDefaultDomain.setValue(bcConfig.ignoreDefault);
      
      // Only load domains if they exist
      if (bcConfig.domains.length > 0) {
        this._setDomainControls(this.bcDomainsArray, bcConfig.domains);
      }

      // Load CRM push/sync preferences
      const crmConfig = await this.userConfigService.loadCrmSyncConfig();
      const notetakerMode = await this.userConfigService.loadNotetakerMode();
      this.optionsForm.patchValue({
        pushMode: crmConfig.pushMode,
        autoSync: crmConfig.autoSync,
        notetakerMode,
      }, { emitEvent: false });

      await this.loadCrmConnectionStatus();

      this.optionsForm.markAsPristine();
    } catch (error) {
      console.error('Error loading config:', error);
      this.notificationService.notify('Failed to load settings');
    } finally {
      this.isLoading = false;
    }
  }

  private _setDomainControls(formArray: FormArray, domains: string[]): void {
    formArray.clear();
    this.bcDomainEnabledState.clear();
    domains.forEach((domain, index) => {
      // Determine if this is for ignored domains or bc domains
      const isIgnoredDomains = formArray === this.ignoredDomainsArray;
      const control = isIgnoredDomains 
        ? this.createStrictDomainControl(domain)
        : this.createUrlControl(domain);
      formArray.push(control);
      if (!isIgnoredDomains) {
        this.bcDomainEnabledState.set(index, true);
      }
    });
  }

  private createStrictDomainControl(value: string = ''): FormControl {
    return this.fb.control(value, [
      Validators.pattern(/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/)
    ]);
  }

  private createUrlControl(value: string = ''): FormControl {
    // Relaxed validation - accepts domains or full URLs
    return this.fb.control(value);
  }

  toggleDefaultDomain(): void {
    // Invert the logic: checked = include (false), unchecked = ignore (true)
    const currentValue = this.bcIgnoreDefaultDomain.value;
    this.bcIgnoreDefaultDomain.setValue(!currentValue);
    this.optionsForm.markAsDirty();
  }

  addNewBcDomain(): void {
    const url = this.newBcDomain.trim();
    if (!url) return;

    // Check for duplicates
    const existingUrls = this.bcDomainsArray.controls.map(c => c.value.toLowerCase());
    if (existingUrls.includes(url.toLowerCase())) {
      this.notificationService.notify('URL already exists');
      return;
    }

    // No strict validation - accept any non-empty string
    this.bcDomainsArray.push(this.createUrlControl(url));
    const newIndex = this.bcDomainsArray.length - 1;
    this.bcDomainEnabledState.set(newIndex, true);
    this.newBcDomain = '';
    this.optionsForm.markAsDirty();
    this.optionsForm.updateValueAndValidity();
  }

  addNewIgnoredDomain(): void {
    const domain = this.newIgnoredDomain.trim();
    if (!domain) return;

    // Check for duplicates
    const existingDomains = this.ignoredDomainsArray.controls.map(c => c.value.toLowerCase());
    if (existingDomains.includes(domain.toLowerCase())) {
      this.notificationService.notify('Domain already exists');
      return;
    }

    // Strict domain validation
    const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!domainPattern.test(domain)) {
      this.notificationService.notify('Invalid domain format. Use format: company.com');
      return;
    }

    this.ignoredDomainsArray.push(this.createStrictDomainControl(domain));
    this.newIgnoredDomain = '';
    this.optionsForm.markAsDirty();
    this.optionsForm.updateValueAndValidity();
  }

  removeIgnoredDomainField(index: number): void {
    this.ignoredDomainsArray.removeAt(index);
    this.optionsForm.markAsDirty();
    this.optionsForm.updateValueAndValidity();
  }

  toggleBcDomain(index: number): void {
    const currentState = this.bcDomainEnabledState.get(index) ?? true;
    this.bcDomainEnabledState.set(index, !currentState);
    this.optionsForm.markAsDirty();
    this.optionsForm.markAsTouched();
  }

  isBcDomainEnabled(index: number): boolean {
    return this.bcDomainEnabledState.get(index) ?? true;
  }

  onDomainChange(): void {
    // Let valueChanges handle the save, but we can explicitly set saveState to 'saving' immediately if we want
    // But debounce will cover it. Just need to update validity if needed.
  }

  async save(): Promise<void> {
    if (this.optionsForm.invalid || this.optionsForm.pristine) {
      return;
    }

    if (this.saveTimeout) clearTimeout(this.saveTimeout);
    this.saveState = 'saving';
    this.isSaving = true;
    try {
      // Save ignored domains
      const ignoredDomains = this.ignoredDomainsArray.controls
        .map(c => c.value)
        .filter(v => v && v.trim());
      
      const uniqueIgnoredDomains = [...new Set(ignoredDomains)];
      await this.userConfigService.saveIgnoredDomains(uniqueIgnoredDomains);

      // Save battle card domains - only save enabled ones
      const bcDomains = this.bcDomainsArray.controls
        .map((c, index) => ({ value: c.value, enabled: this.bcDomainEnabledState.get(index) ?? true }))
        .filter(d => d.enabled && d.value && d.value.trim())
        .map(d => d.value);
      
      const uniqueBcDomains = [...new Set(bcDomains)];
      const bcIgnoreDefault = this.bcIgnoreDefaultDomain.value;
      
      await this.userConfigService.saveBattleCardDomains(bcIgnoreDefault, uniqueBcDomains);

      // Save CRM push/sync settings
      const pushMode = (this.optionsForm.get('pushMode')?.value === 'tasks' ? 'tasks' : 'notes') as 'notes' | 'tasks';
      const autoSync = !!this.optionsForm.get('autoSync')?.value;
      await this.userConfigService.saveCrmSyncConfig(pushMode, autoSync);

      // Save notetaker mode
      const notetakerMode = !!this.optionsForm.get('notetakerMode')?.value;
      await this.userConfigService.saveNotetakerMode(notetakerMode);

      // Remove disabled domains from the form array after successful save
      for (let i = this.bcDomainsArray.length - 1; i >= 0; i--) {
        if (!this.bcDomainEnabledState.get(i)) {
          this.bcDomainsArray.removeAt(i);
          this.bcDomainEnabledState.delete(i);
        }
      }

      // Reindex the enabled state map
      const newEnabledState = new Map<number, boolean>();
      let newIndex = 0;
      this.bcDomainsArray.controls.forEach((_, i) => {
        newEnabledState.set(newIndex, true);
        newIndex++;
      });
      this.bcDomainEnabledState = newEnabledState;

      this.optionsForm.markAsPristine();
      this.saveState = 'saved';
      this.saveTimeout = setTimeout(() => {
        this.saveState = 'idle';
      }, 2000);
      
      this.amplitudeService.trackEvent('settings_saved', {
        ignored_domains_count: uniqueIgnoredDomains.length,
        bc_domains_count: uniqueBcDomains.length,
        bc_ignore_default: bcIgnoreDefault,
        push_mode: pushMode,
        auto_sync: autoSync,
        notetaker_mode: notetakerMode
      });
    } catch (error) {
      console.error('Error saving config:', error);
      this.notificationService.notify('Failed to save settings');
      this.saveState = 'idle';
    } finally {
      this.isSaving = false;
    }
  }

  onBcIgnoreDefaultToggle(): void {
    this.optionsForm.markAsDirty();
  }

  isCrmConnected(provider: CrmProvider): boolean {
    return this.connectedCrmProvider === provider;
  }

  isAnyCrmConnected(): boolean {
    return !!this.connectedCrmProvider;
  }

  isCrmLocked(provider: CrmProvider): boolean {
    return !!this.connectedCrmProvider && this.connectedCrmProvider !== provider;
  }

  isCrmConnectDisabled(provider: CrmProvider): boolean {
    if (this.isCrmActionLoading) {
      return true;
    }

    return this.isCrmConnected(provider) || this.isCrmLocked(provider);
  }

  getCrmConnectButtonLabel(provider: CrmProvider): string {
    if (this.isCrmActionLoading && this.crmActionProvider === provider && this.crmActionType === 'connect') {
      return 'Connecting...';
    }

    if (this.isCrmConnected(provider)) {
      return `Connected to ${this.CRM_LABELS[provider]}`;
    }

    return `Connect ${this.CRM_LABELS[provider]}`;
  }

  getCrmDisconnectButtonLabel(provider: CrmProvider): string {
    if (this.isCrmActionLoading && this.crmActionProvider === provider && this.crmActionType === 'disconnect') {
      return 'Disconnecting...';
    }

    return `Disconnect ${this.CRM_LABELS[provider]}`;
  }

  async connectCrm(provider: CrmProvider): Promise<void> {
    if (this.isCrmConnectDisabled(provider)) {
      return;
    }

    this.isCrmActionLoading = true;
    this.crmActionProvider = provider;
    this.crmActionType = 'connect';

    try {
      const authUrl = await this.userConfigService.requestCrmConnectUrl(provider);
      this.amplitudeService.trackEvent('crm_connect_started', { provider });
      globalThis.location.href = authUrl;
    } catch (error) {
      console.error(`Error connecting ${provider}:`, error);
      this.notificationService.notify(`Failed to connect ${this.CRM_LABELS[provider]}`);
    } finally {
      this.isCrmActionLoading = false;
      this.crmActionProvider = null;
      this.crmActionType = null;
    }
  }

  async disconnectCrm(provider: CrmProvider): Promise<void> {
    if (!this.isCrmConnected(provider) || this.isCrmActionLoading) {
      return;
    }

    this.isCrmActionLoading = true;
    this.crmActionProvider = provider;
    this.crmActionType = 'disconnect';

    try {
      await this.userConfigService.disconnectCrmProvider(provider);
      await this.loadCrmConnectionStatus();
      this.notificationService.notify(`${this.CRM_LABELS[provider]} disconnected successfully`);
      this.amplitudeService.trackEvent('crm_disconnected', { provider });
    } catch (error) {
      console.error(`Error disconnecting ${provider}:`, error);
      this.notificationService.notify(`Failed to disconnect ${this.CRM_LABELS[provider]}`);
    } finally {
      this.isCrmActionLoading = false;
      this.crmActionProvider = null;
      this.crmActionType = null;
    }
  }

  private async loadCrmConnectionStatus(): Promise<void> {
    this.connectedCrmProvider = await this.userConfigService.loadConnectedCrmProvider();
  }
}
